import { useState, useEffect, useCallback, useMemo } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { FREE_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';

import { createLocalExpirableStorageProvider } from '@jira/platform__browser-storage-providers/src/controllers/local-storage/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';

const CONFLUENCE_ADD_PRODUCT_PERMISSION_STORAGE_KEY = 'confluence-add-product-permission';

type Status = {
	canProvisionConfluenceEndUser: boolean;
	fetchStatus: 'pending' | 'resolved' | 'failed';
};

type Props = {
	skip?: boolean;
};

export const localStorageManager = (): {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	getLocalStorage: () => any;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	setLocalStorage: (value: Status | undefined) => any;
} => {
	const localStorageProvider = createLocalExpirableStorageProvider(
		'placeholderTemplateAddProductPermissionStorage',
	);

	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	const getLocalStorage = (): any =>
		localStorageProvider.get(CONFLUENCE_ADD_PRODUCT_PERMISSION_STORAGE_KEY);
	const LOCAL_STORAGE_EXPIRY_MS = 24 * 60 * 60 * 1000; // one day
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	const setLocalStorage = (value: Status | undefined): any =>
		// @browser-storage setter
		localStorageProvider.set(
			CONFLUENCE_ADD_PRODUCT_PERMISSION_STORAGE_KEY,
			value,
			Date.now() + LOCAL_STORAGE_EXPIRY_MS,
		);

	return { getLocalStorage, setLocalStorage };
};

export const useUserHasAddProductPermissions = ({ skip = false }: Props): Status => {
	const cloudId = useCloudId();
	const appEditions = useAppEditions();
	const { getLocalStorage, setLocalStorage } = useMemo(() => localStorageManager(), []);
	const cachedStatus = getLocalStorage();

	const initialStatus = cachedStatus || {
		canProvisionConfluenceEndUser: false,
		fetchStatus: 'pending',
	};
	const [status, setStatus] = useState<Status>(initialStatus);

	const handleFetchPermissions = useCallback(async (): Promise<void> => {
		try {
			const newStatus: Status = {
				canProvisionConfluenceEndUser: appEditions.software === FREE_EDITION,
				fetchStatus: 'resolved',
			};

			setStatus(newStatus);
			setLocalStorage(newStatus);
		} catch (error: unknown) {
			fireErrorAnalytics({
				meta: {
					id: CONFLUENCE_ADD_PRODUCT_PERMISSION_STORAGE_KEY,
					packageName: 'jiraIssueViewBase',
					teamName: 'starburst',
				},
				error: new Error(
					`Failed to get ${CONFLUENCE_ADD_PRODUCT_PERMISSION_STORAGE_KEY} status: ${error}`,
				),
			});

			const errorStatus: Status = {
				canProvisionConfluenceEndUser: false,
				fetchStatus: 'failed',
			};
			setStatus(errorStatus);
			setLocalStorage(errorStatus);
		}
	}, [appEditions.software, setLocalStorage]);

	useEffect(() => {
		if (!skip && cloudId && !cachedStatus) {
			handleFetchPermissions();
		}
	}, [cloudId, skip, cachedStatus, handleFetchPermissions]);

	return status;
};
